// Name und Adresse nach dem Start berichtigen (seit 19.10.2026, Augentest am
// iPhone: Nach „Besichtigung starten“ kam man nicht mehr an Kunde und
// Adresse). Dasselbe Formular wie „Neue Besichtigung“, vorbefüllt, mit
// „Speichern“; dieselben Pflichtfelder. Gespeichert wird in die
// Arbeitskopie, und der Eintrag „aufnahme“ geht noch einmal ans Büro
// (arbeitskopie.ts, kundeAendern). Ist die Besichtigung schon abgeschlossen
// und gesendet, geht es nicht mehr – das sagt die Seite.
//
// Adresse: #/aufnahme/<kennung>/kunde

import { useState } from "react";
import { STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppKopf, toastZeigen, Tipp } from "@happy-energy/oberflaeche";
import { kundeAendern, kundeAenderbar, kundeAngaben, type KundeAngaben } from "../arbeitskopie.ts";
import { speicher, type Aufnahme, type Station } from "../speicher.ts";
import { useLive, ZUM_START } from "./gemeinsam.tsx";
import { KundeFormular, type FormularKennungen } from "./NeueBesichtigung.tsx";

async function lesen(aufnahmeId: string): Promise<{ aufnahme: Aufnahme; stationen: Station[] } | null> {
  const aufnahme = await speicher.aufnahmen.get(aufnahmeId);
  if (!aufnahme) return null;
  const stationen = await speicher.stationen.where("aufnahmeId").equals(aufnahmeId).sortBy("nr");
  return { aufnahme, stationen };
}

/** Die Kennungen der Besichtigung – das Formular prüft damit gegen denselben Vertrag wie der Server. */
function kennungenVon(aufnahme: Aufnahme, stationen: Station[]): FormularKennungen {
  return {
    aufnahmeId: aufnahme.id,
    kundeId: aufnahme.kunde.id,
    objektId: aufnahme.objekt.id,
    anlageId: aufnahme.anlage.id,
    stationIds: STATIONEN.map((s) => stationen.find((x) => x.art === s.schluessel)?.id ?? ""),
  };
}

function Formular({ ich, aufnahme, stationen }: { ich: IchAntwort; aufnahme: Aufnahme; stationen: Station[] }) {
  // Einmal beim Öffnen: Was danach am Gerät geschieht (Senden), soll das Getippte nicht überschreiben.
  const [anfang] = useState(() => kundeAngaben(aufnahme));
  const [kennungen] = useState(() => kennungenVon(aufnahme, stationen));
  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;

  async function speichern(werte: KundeAngaben) {
    const ergebnis = await kundeAendern(aufnahme.id, werte);
    if (ergebnis === "gesperrt") {
      toastZeigen("Diese Besichtigung ist schon gesendet – ändern geht hier nicht mehr.", "fehler");
      return;
    }
    if (ergebnis === "geaendert") toastZeigen("Name und Adresse gespeichert");
    // Ersetzen statt dazulegen: „Zurück“ in der Übersicht führt dann zur Startseite, nicht wieder ins Formular.
    window.location.replace(zurUebersicht);
  }

  return (
    <KundeFormular
      ich={ich}
      titel="Kunde und Adresse"
      zurueck={{ adresse: zurUebersicht, name: "Übersicht" }}
      kennungen={kennungen}
      anfang={anfang}
      neu={false}
      knopf="Speichern"
      abschicken={speichern}
    />
  );
}

export function KundeAendern({ ich, aufnahmeId }: { ich: IchAntwort; aufnahmeId: string }) {
  const daten = useLive(() => lesen(aufnahmeId), aufnahmeId);
  if (daten === undefined) return <div className="app-seite" />;
  if (daten === null || !kundeAenderbar(daten.aufnahme)) {
    return (
      <div className="app-seite">
        <AppKopf
          umgebung={ich.umgebung}
          zurueck={daten ? { adresse: `#/aufnahme/${aufnahmeId}`, name: "Übersicht" } : ZUM_START}
          titel="Kunde und Adresse"
          gross={daten ? daten.aufnahme.kunde.name : "Nicht gefunden"}
        />
        <main className="app-inhalt">
          <Tipp art="grau">
            {daten
              ? "Diese Besichtigung ist schon abgeschlossen und gesendet – Name und Adresse lassen sich hier nicht mehr ändern."
              : "Diese Besichtigung ist auf diesem Gerät nicht gespeichert."}
          </Tipp>
        </main>
      </div>
    );
  }
  return <Formular ich={ich} aufnahme={daten.aufnahme} stationen={daten.stationen} />;
}
